import { TabsWithUnderline } from "@truevault/aquilo";
import React from "react";
import { useHistory } from "react-router";
import { Organization, OrganizationUser } from "../../../common/models";
import { usePrimaryOrganization, usePrimaryOrganizationId } from "../../hooks";
import { useSelfOrganizationUser } from "../../hooks/useOrganization";

type Tabs = "settings" | "users" | "billing" | "domain" | "advanced";

const TAB_OPTIONS: { label: string; value: Tabs }[] = [
  { label: "Business Profile", value: "settings" },
  { label: "Users", value: "users" },
  { label: "Billing", value: "billing" },
  { label: "Domain", value: "domain" },
  { label: "Advanced", value: "advanced" },
];

/**
 * MCP tokens are bound to a real `organization_user` row, so this is shown only when MCP is on
 * *and* the signed-in user is a direct member of the org they are viewing — not a parent-org admin
 * looking at a client org.
 *
 * `useSelfOrganizationUser` includes parent-org admins (extended orgs), but their membership row
 * still points at the parent, so comparing `currentOrgUser.organization` to the viewed org is the
 * direct-member check.
 */
export const isMcpEnabled = (
  organization: Organization | undefined,
  currentOrgUser: OrganizationUser | undefined,
): boolean =>
  organization?.featureMcp === true &&
  currentOrgUser?.organization === organization.id &&
  currentOrgUser?.status === "ACTIVE";

export const SettingsTabs = ({ current = "settings" }: { current?: Tabs }) => {
  const history = useHistory();
  const orgId = usePrimaryOrganizationId();
  const [organization] = usePrimaryOrganization();
  const currentOrgUser = useSelfOrganizationUser();
  const tabOptions = TAB_OPTIONS.filter((t) => {
    if (t.value === "users" && currentOrgUser?.role !== "ADMIN") {
      return false;
    }
    if (t.value === "advanced" && !isMcpEnabled(organization, currentOrgUser)) {
      return false;
    }
    return true;
  });

  return (
    <div className="tw-boundary">
      <TabsWithUnderline
        tabs={tabOptions.map((t) => ({
          name: t.label,
          current: t.value === current,
          onClick: () => {
            const path = t.value === "settings" ? "" : t.value;
            history.push(`/organization/${orgId}/settings/${path}`.replace(/\/$/, ""));
          },
        }))}
      />
    </div>
  );
};
